import { GLTF } from "three/examples/jsm/Addons.js"
import { Fn } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import curvePath from "./assets/highway-path.json"

type HighwayProps = {
  scene: THREE.Scene
  debugFolder?: FolderApi
  resources: { [key: string]: GLTF }
}

const dummyObject = new THREE.Object3D()

export const ambientLight = Fn(([lightColor, lightIntensity]: [any, any]) => {
  return lightColor.mul(lightIntensity)
})

export default class Highway {
  scene: THREE.Scene
  debugFolder?: FolderApi
  mesh!: THREE.InstancedMesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>
  curve!: THREE.CatmullRomCurve3

  offsets: THREE.Vector3[] = []

  count: number
  positions: number[] = []
  speed: number = 0.0003
  scale: number = 0.4

  // Reusable Vector3 to avoid allocations in update loop
  private tempPoint = new THREE.Vector3()
  private tempTangent = new THREE.Vector3()
  private tempLookAt = new THREE.Vector3()

  constructor({ scene, resources, debugFolder }: HighwayProps) {
    this.scene = scene
    this.debugFolder = debugFolder

    const model = resources.blender.scene?.getObjectByName(
      "Car1",
    ) as THREE.Object3D

    this.count = 100
    this.scale = 0.1

    this.setCurve()
    this.setMesh(model)

    this.setupDebug()
  }

  setCurve() {
    const vertices = []
    for (let i = 0; i < curvePath.points.length; i++) {
      const x = curvePath.points[i].x
      const y = curvePath.points[i].y
      const z = curvePath.points[i].z

      vertices[i] = new THREE.Vector3(x, y, z)
    }
    this.curve = new THREE.CatmullRomCurve3(vertices)
  }

  setMesh(model: THREE.Object3D) {
    // Find the mesh - it could be the model itself or a child
    let carMesh: THREE.Mesh<
      THREE.BufferGeometry,
      THREE.MeshStandardMaterial
    > | null = null

    if (model instanceof THREE.Mesh) {
      carMesh = model
    } else {
      model.traverse((child) => {
        if (child instanceof THREE.Mesh && !carMesh) {
          carMesh = child
        }
      })
    }

    if (!carMesh) {
      console.error("No mesh found in Car1 model")
      return
    }

    carMesh.material.emissive = new THREE.Color("#f3faff")
    carMesh.material.emissiveIntensity = 10

    // Initialize positions and offsets for all instances
    for (let i = 0; i < this.count; i++) {
      const t = i / this.count
      this.positions[i] = t

      this.offsets[i] = new THREE.Vector3(
        Math.random() * 5 - 2.5,
        Math.random() * 2 - 1.25,
        Math.random() * 5 - 2.5,
      )
    }

    // Create InstancedMesh from the single mesh
    this.mesh = new THREE.InstancedMesh(
      carMesh.geometry,
      carMesh.material,
      this.count,
    ) as THREE.InstancedMesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>

    // Mark instance matrix for frequent updates
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    // Set initial matrices for all instances
    for (let i = 0; i < this.count; i++) {
      const t = this.positions[i]
      this.tempPoint.copy(this.curve.getPoint(t)).add(this.offsets[i])

      // Get tangent to orient the object along the curve
      this.curve.getTangent(t, this.tempTangent)

      dummyObject.position.copy(this.tempPoint)
      dummyObject.scale.set(this.scale, this.scale, this.scale)

      // Orient the object along the curve direction
      this.tempLookAt.copy(this.tempPoint).add(this.tempTangent)
      dummyObject.lookAt(this.tempLookAt)

      dummyObject.updateMatrix()
      this.mesh.setMatrixAt(i, dummyObject.matrix)
    }

    this.mesh.instanceMatrix.needsUpdate = true
    this.scene.add(this.mesh)
  }

  update() {
    for (let i = 0; i < this.count; i++) {
      this.positions[i] -= this.speed

      if (this.positions[i] <= 0) {
        this.positions[i] = 1
      }

      const t = this.positions[i]
      this.tempPoint.copy(this.curve.getPoint(t)).add(this.offsets[i])

      // Get tangent to orient the object along the curve
      this.curve.getTangent(t, this.tempTangent)

      dummyObject.position.copy(this.tempPoint)
      dummyObject.scale.set(this.scale, this.scale, this.scale)

      // Orient the object along the curve direction
      this.tempLookAt.copy(this.tempPoint).add(this.tempTangent)
      dummyObject.lookAt(this.tempLookAt)

      dummyObject.updateMatrix()
      this.mesh.setMatrixAt(i, dummyObject.matrix)
    }
  }

  setupDebug() {
    if (this.debugFolder && this.mesh) {
      const folder = this.debugFolder.addFolder({
        title: "🛣 Highway",
        expanded: false,
      })

      folder.addBinding(this.mesh.material, "emissiveIntensity", {
        min: 0,
        max: 10,
        step: 0.01,
      })

      const emissiveSettings = {
        emissive: `#${this.mesh.material.emissive.getHexString()}`,
      }

      folder
        .addBinding(emissiveSettings, "emissive", {
          view: "color",
        })
        .on("change", (ev) => {
          this.mesh.material.emissive = new THREE.Color(ev.value)
        })
    }
  }
}
